import { Component, OnInit, inject } from '@angular/core';
import { AppToastService } from 'app/core/services/app-toast.service';
import { LimitOffsetPaging, Sort } from '../../../core/models/paged-results/limit-offset-paging.model';
import { PrescriberIncorrectInformationRequest } from '../../../core/models/prescriber-incorrect-information-request.model';
import {
  PrescriberIncorrectInformationRequestFilters,
  PrescriberIncorrectInformationRequestQueueSettings,
} from '../../../core/models/user-settings/prescriber-incorrect-information-request-settings.model';
import {
  PrescriberIncorrectInformationRequestsService,
  PagedPrescriberIncorrectInformationRequests,
} from '../../../core/services/prescriber-incorrect-information-requests.service';
import { UserSettings } from '../../../core/enums/user-settings.enum';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import { FormGroup, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SpecialtyBadgesComponent } from '../../../shared/components/specialty-badges/specialty-badges.component';

enum PrescriberIncorrectInformationRequestsSortBy {
  createdAtDesc = 'created_at_desc',
  createdAtAsc = 'created_at_asc',
}

@Component({
  selector: 'app-capture-admin-prescriber-incorrect-information-requests',
  templateUrl: './capture-admin-prescriber-incorrect-information-requests.component.html',
  styleUrls: ['./capture-admin-prescriber-incorrect-information-requests.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    PaginationHeaderComponent,
    PrescriberNameComponent,
    PaginationComponent,
    MatProgressSpinner,
    DatePipe,
    SpecialtyBadgesComponent
  ],
})
export class CaptureAdminPrescriberIncorrectInformationRequestsComponent implements OnInit {
  private prescriberIncorrectInformationRequestsService = inject(PrescriberIncorrectInformationRequestsService);
  private toastService = inject(AppToastService);
  private userSettingsService = inject(UserSettingsService);
  private formBuilder = inject(FormBuilder);

  prescriberIncorrectInformationRequests: PrescriberIncorrectInformationRequest[] = [];
  paging: LimitOffsetPaging = LimitOffsetPaging.empty;
  filters: PrescriberIncorrectInformationRequestFilters;
  sort: Sort;
  loading = true;
  formGroup: FormGroup;

  filterOptions = [
    { value: 'needs_review', display: 'Needs Review' },
    { value: 'resolved', display: 'Resolved' },
  ];

  sortByOptions = [
    { value: PrescriberIncorrectInformationRequestsSortBy.createdAtDesc, display: 'Date Reported - Newest First' },
    { value: PrescriberIncorrectInformationRequestsSortBy.createdAtAsc, display: 'Date Reported - Oldest First' },
  ];

  static sortByValueToSort = {
    [PrescriberIncorrectInformationRequestsSortBy.createdAtDesc]: new Sort('created_at', 'desc'),
    [PrescriberIncorrectInformationRequestsSortBy.createdAtAsc]: new Sort('created_at', 'asc'),
  };

  ngOnInit() {
    this.loadQueueSettings();
    this.loadRequests();
    this.initFormGroup();
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadRequests();
    this.saveQueueSettings();
  }

  onPageChange(page: number) {
    this.loadRequests(page);
    this.saveQueueSettings();
  }

  resolve($event: Event, request: PrescriberIncorrectInformationRequest) {
    $event.preventDefault();

    this.prescriberIncorrectInformationRequestsService.update(request, { resolved: true }).subscribe(() => {
      this.toastService.show('Request resolved!', { cssClass: 'bg-success-subtle' });
      this.loadRequests(this.paging.currentPage);
    });
  }

  unresolve($event: Event, request: PrescriberIncorrectInformationRequest) {
    $event.preventDefault();

    this.prescriberIncorrectInformationRequestsService.update(request, { resolved: false }).subscribe(() => {
      this.toastService.show('Request unresolved!', { cssClass: 'bg-success-subtle' });
      this.loadRequests(this.paging.currentPage);
    });
  }

  private loadQueueSettings() {
    const queueSettings = this.userSettingsService.get<PrescriberIncorrectInformationRequestQueueSettings>(
      UserSettings.captureAdminPrescriberIncorrectInformationRequestQueueSettings
    );

    if (queueSettings) {
      this.filters = queueSettings.filters;
      this.paging = queueSettings.paging;
      this.sort = queueSettings.sort;
    } else {
      this.filters = new PrescriberIncorrectInformationRequestFilters();
      this.sort = CaptureAdminPrescriberIncorrectInformationRequestsComponent.sortByValueToSort[
        PrescriberIncorrectInformationRequestsSortBy.createdAtDesc
      ];
    }
  }

  private saveQueueSettings() {
    const queueSettings = new PrescriberIncorrectInformationRequestQueueSettings();
    queueSettings.filters = this.filters;
    queueSettings.paging = this.paging;
    queueSettings.sort = CaptureAdminPrescriberIncorrectInformationRequestsComponent.sortByValueToSort[
      this.formGroup.value.sort
    ];

    this.userSettingsService.save<PrescriberIncorrectInformationRequestQueueSettings>(
      UserSettings.captureAdminPrescriberIncorrectInformationRequestQueueSettings,
      queueSettings
    );
  }

  private loadRequests(page = this.paging.currentPage, showLoading = true) {
    if (showLoading) this.loading = true;

    const paging = { page, pageSize: this.paging.pageSize };

    this.prescriberIncorrectInformationRequestsService
      .getList(paging, this.filters, this.sort)
      .subscribe((result: PagedPrescriberIncorrectInformationRequests) => {
        this.prescriberIncorrectInformationRequests = result.prescriberIncorrectInformationRequests;
        this.paging = result.meta.paging;
        this.loading = false;
      });
  }

  private initFormGroup() {
    this.formGroup = this.formBuilder.group({
      sort: `${this.sort.col}_${this.sort.dir}`,
      filters: this.formBuilder.group({
        status: [this.filters.status],
      }),
    });

    this.formGroup.valueChanges.subscribe(_value => {
      this.paging.currentPage = 1;

      const formValue = this.formGroup.value;

      this.sort = CaptureAdminPrescriberIncorrectInformationRequestsComponent.sortByValueToSort[formValue.sort];

      this.filters = new PrescriberIncorrectInformationRequestFilters();
      this.filters.status = formValue.filters.status;

      this.saveQueueSettings();
      this.loadRequests();
    });
  }
}
